import { type ComponentPropsWithoutRef } from "react"
import "./ReactionPicker.scss"
import { useAppContext } from "../../../hooks/appContext"
import {
    useDeleteReactionMutation,
    useUpdateReactionMutation,
} from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import { EmojiData, ReactionEmoji } from "../types"

interface ReactionPickerProps extends ComponentPropsWithoutRef<"div"> {
    reactions: Record<string, string> | undefined
    messageId: string
    closeCallback: () => void
}

export const getReactionName = (reaction: ReactionEmoji): string => {
    switch (reaction) {
        case ReactionEmoji.heart:
            return "Heart"
        case ReactionEmoji.heartEyes:
            return "HeartEyes"
        case ReactionEmoji.thumbsUp:
            return "ThumbsUp"
        case ReactionEmoji.fire:
            return "Fire"
        case ReactionEmoji.laughing:
            return "Laughing"
        default:
            return "None"
    }
}

export function ReactionPicker({
    reactions,
    messageId,
    closeCallback,
}: ReactionPickerProps) {
    const [updateReaction] = useUpdateReactionMutation()
    const [deleteReaction] = useDeleteReactionMutation()
    let selected = ReactionEmoji.none
    const { context: appContext } = useAppContext()
    const username = appContext.logged_in_user?.username
    if (reactions !== undefined) {
        const reaction =
            username !== undefined ? reactions[username] : undefined
        if (reaction !== undefined) {
            selected =
                EmojiData.get(reaction.split(":")[0] ?? "") ??
                ReactionEmoji.none
        }
    }
    const selectEmoji = (e: React.MouseEvent, reaction: ReactionEmoji) => {
        e.preventDefault()
        if (selected === reaction) {
            deleteReaction({ message_id: messageId })
                .then(() => {
                    addPageAction("DmUpdateReaction", {
                        reactionAction: "delete",
                        reactionId: getReactionName(reaction),
                    })
                })
                .catch(() => {})
        } else {
            const reactionCode = reaction
                .codePointAt(0)
                ?.toString(16)
                .toUpperCase()
            if (reactionCode !== undefined) {
                updateReaction({
                    message_id: messageId,
                    reaction: reactionCode,
                })
                    .then(() => {
                        addPageAction("DmUpdateReaction", {
                            reactionAction:
                                selected === ReactionEmoji.none
                                    ? "add"
                                    : "replace",
                            reactionId: getReactionName(reaction),
                        })
                    })
                    .catch(() => {})
            }
        }
        closeCallback()
    }
    return (
        <div className="message-reaction-picker">
            <div className="reaction-emojis">
                <span
                    className={`emoji-option ${selected === ReactionEmoji.heart}`}
                    onClick={(e) => selectEmoji(e, ReactionEmoji.heart)}
                >
                    {ReactionEmoji.heart}
                </span>
                <span
                    className={`emoji-option ${selected === ReactionEmoji.heartEyes}`}
                    onClick={(e) => selectEmoji(e, ReactionEmoji.heartEyes)}
                >
                    {ReactionEmoji.heartEyes}
                </span>
                <span
                    className={`emoji-option ${selected === ReactionEmoji.thumbsUp}`}
                    onClick={(e) => selectEmoji(e, ReactionEmoji.thumbsUp)}
                >
                    {ReactionEmoji.thumbsUp}
                </span>
                <span
                    className={`emoji-option ${selected === ReactionEmoji.fire}`}
                    onClick={(e) => selectEmoji(e, ReactionEmoji.fire)}
                >
                    {ReactionEmoji.fire}
                </span>
                <span
                    className={`emoji-option ${selected === ReactionEmoji.laughing}`}
                    onClick={(e) => selectEmoji(e, ReactionEmoji.laughing)}
                >
                    {ReactionEmoji.laughing}
                </span>
            </div>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="10"
                viewBox="0 0 19 10"
                fill="none"
            >
                <path
                    d="M11.9549 8.86737C11.1612 9.74222 9.78621 9.74222 8.9925 8.86737L0.947442 -1.66563e-06L20 0L11.9549 8.86737Z"
                    fill="#FFFFFF"
                />
            </svg>
        </div>
    )
}
